import React, { useEffect, useState, useRef } from 'react';

interface AnimatedWindowProps {
  children: React.ReactNode;
  isOpen: boolean;
  className?: string;
}

const AnimatedWindow: React.FC<AnimatedWindowProps> = ({ children, isOpen, className = '' }) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const [entering, setEntering] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setShouldRender(true);
      setClosing(false);
      setEntering(false);
      // Double RAF ensures DOM is ready before animation starts
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEntering(true);
        });
      });
    } else {
      setClosing(true);
      setEntering(false);
      timerRef.current = setTimeout(() => {
        setShouldRender(false);
        setClosing(false);
      }, 300);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [isOpen]);

  if (!shouldRender) return null;

  const animationClass = closing 
    ? 'animate-zoom-out' 
    : entering 
      ? 'animate-zoom-in' 
      : '';

  const hiddenStyle = !closing && !entering 
    ? { opacity: 0, transform: 'scale(0.01)' } 
    : undefined;

  return (
    <div
      className={`${className} transform-gpu origin-center ${animationClass}`}
      style={hiddenStyle}
    >
      {children}
    </div>
  );
};

export default AnimatedWindow;
